import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
  useAppForm,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, X } from "lucide-react";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { RiAddLine } from "react-icons/ri";
import { useDebounceCallback } from "usehooks-ts";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { AdminLogosQuery } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/logos")({
  head: () => opsHead("Logos"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const logosDocument = graphql(`
  query AdminLogos($after: String, $first: Int!, $filter: LogoFilter) {
    logos(after: $after, first: $first, filter: $filter) {
      pageInfo {
        endCursor
        hasNextPage
      }
      logos {
        id
        slug
        url
        storageObject {
          id
        }
      }
    }
  }
`);

const createStaticAssetFileDocument = graphql(`
  mutation CreateStaticAssetFileForLogo($input: CreateStaticAssetFileInput!) {
    createStaticAssetFile(input: $input) {
      storageObject {
        id
      }
      uploadUrl
    }
  }
`);

const updateStaticAssetFileDocument = graphql(`
  mutation UpdateStaticAssetFileForLogo($input: UpdateStaticAssetFileInput!) {
    updateStaticAssetFile(input: $input) {
      storageObject {
        id
      }
      uploadUrl
    }
  }
`);

const upsertLogoDocument = graphql(`
  mutation UpsertLogo($input: UpsertLogoInput!) {
    upsertLogo(input: $input) {
      logo {
        id
        slug
        url
        storageObject {
          id
        }
      }
    }
  }
`);

const deleteLogoDocument = graphql(`
  mutation DeleteLogo($input: DeleteLogoInput!) {
    deleteLogo(input: $input) {
      success
    }
  }
`);

const filterSchema = z.object({
  searchTerm: z.string().nullish(),
});

const formSchema = z.object({
  slug: z.string().trim().min(1),
});

type LogoRow = NonNullable<AdminLogosQuery["logos"]>["logos"][number];

type FormValues = z.input<typeof formSchema>;

function View() {
  const { useSearchParams } = useRouter();
  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: filterSchema,
    defaultState: {},
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultCreateOpen = searchParams.has("default-create-open");
  const [editingRow, setEditingRow] = useState<LogoRow | null>(null);
  const [deletingRow, setDeletingRow] = useState<LogoRow | null>(null);
  const [upsertDialogOpen, setUpsertDialogOpen] = useState(defaultCreateOpen);
  const [createStaticAssetFile] = useMutation(createStaticAssetFileDocument);
  const [updateStaticAssetFile] = useMutation(updateStaticAssetFileDocument);
  const [upsertLogo] = useMutation(upsertLogoDocument);
  const [deleteLogo, { loading: deleteLogoLoading }] =
    useMutation(deleteLogoDocument);

  useEffect(() => {
    if (defaultCreateOpen) {
      setSearchParams((p) => {
        p.delete("default-create-open");
        return p;
      });
    }
  }, [defaultCreateOpen, setSearchParams]);

  const columns = useMemo<ColumnDef<LogoRow>[]>(
    () => [
      {
        id: "preview",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Preview" />
        ),
        cell: ({ row }) => (
          <img
            className="size-10 rounded border bg-white object-contain p-1"
            alt={row.original.slug}
            src={row.original.url}
            id={getDomIDForLogo(row.original)}
          />
        ),
        enableSorting: false,
      },
      {
        id: "slug",
        accessorKey: "slug",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Slug" />
        ),
        enableSorting: false,
        cell(r) {
          return <Txt variant="mono"> {r.row.original.slug}</Txt>;
        },
      },
      {
        id: "url",
        accessorKey: "url",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="URL" />
        ),
        cell: ({ row }) => (
          <a
            href={row.original.url}
            target="_blank"
            rel="noreferrer"
            className="text-info underline-offset-2 hover:underline"
          >
            {row.original.url}
          </a>
        ),
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => <div className="sr-only">Actions</div>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary">
                <MoreHorizontal className="size-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setEditingRow(row.original);
                  setUpsertDialogOpen(true);
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setDeletingRow(row.original);
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableSorting: false,
      },
    ],
    [],
  );

  const baseInput = {
    first: 20,
    filter: {
      searchTerm: filter.searchTerm ?? undefined,
    },
  };

  const { data, fetchMore, loading, networkStatus } = useQuery(logosDocument, {
    variables: {
      after: null,
      ...baseInput,
    },
    notifyOnNetworkStatusChange: true,
  });

  const rows = data?.logos.logos ?? [];
  const nextCursor = data?.logos.pageInfo.hasNextPage
    ? (data.logos.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <AdminPageContainer testId="admin-logos">
      <AdminPageHeader
        title="Logos"
        subtitle="Manage logo slugs and the static assets they resolve to."
        buttons={
          <Button
            leftIcon={<RiAddLine />}
            onClick={() => setUpsertDialogOpen(true)}
            variant="outline"
          >
            Add Logo
          </Button>
        }
      />
      <div className="flex flex-col gap-4">
        <FilterBar filter={filter} onFilterChange={setFilter} />
        <DataTable
          data={rows}
          columns={columns}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualPagination={true}
          hidePagination={true}
          pageSize={10_000}
          className="overflow-hidden border"
        />
        <div className="mt-4 flex w-full justify-center">
          {nextCursor ? (
            <Button
              onClick={() =>
                fetchMore({
                  variables: {
                    ...baseInput,
                    after: nextCursor,
                  },
                })
              }
              variant="outline"
              disabled={loading}
              loading={loading || networkStatus === NetworkStatus.fetchMore}
            >
              Load more
            </Button>
          ) : (
            !!rows.length && (
              <p className="text-secondary text-sm">No more data</p>
            )
          )}
        </div>
      </div>
      <UpsertLogoDialog
        open={upsertDialogOpen}
        row={editingRow}
        onOpenChange={(open) => {
          setUpsertDialogOpen(open);
          if (!open && editingRow) {
            setEditingRow(null);
          }
        }}
        onSubmit={async ({ file, slug }) => {
          const contentType = file.type || "application/octet-stream";
          const fileExtension = file.name.includes(".")
            ? (file.name.split(".").pop()?.toLowerCase() ?? "bin")
            : "bin";
          const safeSlug = slug.replace(/[^a-zA-Z0-9-_]/g, "-");
          const objectKey = `images/logos/${safeSlug}.${fileExtension}`;

          const uploadPayload = editingRow
            ? await updateStaticAssetFile({
                variables: {
                  input: {
                    storageObjectId: editingRow.storageObject.id,
                    fileName: file.name,
                    contentType,
                  },
                },
              }).then((result) => result.data?.updateStaticAssetFile)
            : await createStaticAssetFile({
                variables: {
                  input: {
                    fileName: file.name,
                    objectKey,
                    contentType,
                  },
                },
              }).then((result) => result.data?.createStaticAssetFile);

          if (!uploadPayload?.storageObject?.id) {
            throw new Error("Expected storage object in upload payload");
          }

          const uploadResponse = await fetch(uploadPayload.uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": contentType,
            },
          });

          if (!uploadResponse.ok) {
            throw new Error(
              `Failed to upload file: ${uploadResponse.statusText}`,
            );
          }

          await upsertLogo({
            variables: {
              input: {
                slug,
                storageObjectId: uploadPayload.storageObject.id,
              },
            },
            refetchQueries: "active",
            awaitRefetchQueries: true,
            onCompleted() {
              toast({
                kind: "success",
                title: `Logo ${editingRow ? "updated" : "created"}`,
                autoHide: true,
              });
              setUpsertDialogOpen(false);
              setEditingRow(null);
            },
          });

          if (editingRow) {
            // Force browser to refetch
            const img = document.getElementById(getDomIDForLogo(editingRow));
            if (img && "src" in img && typeof img.src === "string") {
              const baseSrc = img?.src.split("?")[0];
              img.src = baseSrc + "?t=" + new Date().getTime();
            }
          }
        }}
      />
      <Dialog
        open={deletingRow !== null}
        onOpenChange={(open) => {
          if (!open && !deleteLogoLoading) {
            setDeletingRow(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete logo?</DialogTitle>
          </DialogHeader>
          <p className="text-secondary text-sm">
            This will permanently delete the logo
            {deletingRow ? ` "${deletingRow.slug}"` : ""}.
          </p>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="secondary"
              onClick={() => {
                setDeletingRow(null);
              }}
              disabled={deleteLogoLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={deleteLogoLoading}
              disabled={deleteLogoLoading || deletingRow === null}
              onClick={() => {
                if (!deletingRow) {
                  return;
                }

                deleteLogo({
                  variables: {
                    input: {
                      id: deletingRow.id,
                    },
                  },
                  refetchQueries: ["AdminLogos"],
                  awaitRefetchQueries: true,
                  onCompleted() {
                    setDeletingRow(null);
                    toast({
                      kind: "success",
                      title: "Logo deleted",
                      autoHide: true,
                    });
                  },
                  onError(error) {
                    toast({
                      kind: "error",
                      title: "Failed to delete logo",
                      description: error.message,
                      autoHide: false,
                    });
                  },
                });
              }}
            >
              Confirm deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageContainer>
  );
}

function FilterBar({
  filter,
  onFilterChange,
}: {
  filter: z.infer<typeof filterSchema>;
  onFilterChange: (_: z.infer<typeof filterSchema>) => void;
}) {
  const searchTermRef = useRef<HTMLInputElement | null>(null);
  const setSearchTermDebounced = useDebounceCallback(
    (searchTerm: string) =>
      onFilterChange({
        searchTerm: searchTerm.length ? searchTerm : undefined,
      }),
    200,
  );

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="w-64">
        <Input
          ref={searchTermRef}
          defaultValue={filter.searchTerm ?? ""}
          type="search"
          placeholder="Search logos"
          onChange={(event) => {
            setSearchTermDebounced(event.currentTarget.value);
          }}
        />
      </div>
      <Button
        rightIcon={<X />}
        variant="ghost"
        size="sm"
        onClick={() => {
          onFilterChange({
            searchTerm: undefined,
          });
          if (searchTermRef.current) {
            searchTermRef.current.value = "";
          }
        }}
        disabled={!filter.searchTerm}
      >
        Reset
      </Button>
    </div>
  );
}

function UpsertLogoDialog({
  row,
  open,
  onOpenChange,
  onSubmit,
}: {
  row: LogoRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (_: { file: File; slug: string }) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(row?.url ?? null);
  const form = useAppForm({
    defaultValues: {
      slug: row?.slug ?? "",
    } satisfies FormValues,
    validators: {
      onSubmit: formSchema,
    },
    async onSubmit(formProps) {
      const parsed = formSchema.parse(formProps.value);
      if (!file) {
        throw new Error("A logo file is required");
      }
      await onSubmit({
        slug: parsed.slug,
        file,
      });
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        slug: row?.slug ?? "",
      });
      setFile(null);
      setPreviewUrl(row?.url ?? null);
      return;
    }

    form.reset();
    setFile(null);
    setPreviewUrl(null);
  }, [form, open, row]);

  useEffect(() => {
    if (!file) {
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{row ? "Edit Logo" : "New Logo"}</DialogTitle>
        </DialogHeader>
        <form.AppForm>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void form.handleSubmit(event);
            }}
          >
            <form.AppField name="slug">
              {(field) => <field.InputField type="text" label="Slug" />}
            </form.AppField>
            <div className="flex flex-col gap-2">
              <Label htmlFor="logo-file">Logo file</Label>
              {previewUrl ? (
                <img
                  className="h-32 w-full rounded border bg-white object-contain p-3"
                  alt={file?.name || row?.slug || "Logo preview"}
                  src={previewUrl}
                />
              ) : null}
              <Input
                id="logo-file"
                type="file"
                accept="image/*"
                onChange={(event) => {
                  setFile(event.currentTarget.files?.[0] ?? null);
                }}
              />
            </div>
            <form.Subscribe
              selector={(state) => ({
                isSubmitting: state.isSubmitting,
              })}
            >
              {(state) => (
                <Button
                  type="submit"
                  loading={state.isSubmitting}
                  disabled={state.isSubmitting}
                >
                  Save
                </Button>
              )}
            </form.Subscribe>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function getDomIDForLogo(logo: { id: string }): string {
  return `logo-img-${logo.id}`;
}
